'use client';

import Link from 'next/link';
import { Anchor, Badge, Box, Button, Container, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { getCarComparisonLinkProps } from '../../../lib/content/comparisonLinks';
import type { PageWidgetItem } from '../../../types/content';
import {
  getWidgetCardStyle,
  widgetBadgeStyle,
  widgetLinkStyle,
  widgetPrimaryButtonStyles,
  widgetSectionPadding,
  widgetSubtitleStyle,
  widgetTitleStyle,
} from './widgetStyles';
import { MantineNextImage } from '../../ui/MantineNextImage';

export function SplitHighlightWidget({
  badge,
  title,
  body,
  imageUrl,
  imageAlt,
  items,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  mapHref,
}: {
  badge?: string;
  title?: string;
  body?: string;
  imageUrl?: string;
  imageAlt?: string;
  items: PageWidgetItem[];
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  mapHref: (href?: string) => string | undefined;
}) {
  if (!title && !body && !items.length) return null;

  const primaryHref = primaryCtaLabel ? mapHref(primaryCtaHref) : undefined;
  const secondaryHref = secondaryCtaLabel ? mapHref(secondaryCtaHref) : undefined;

  return (
    <Container size="xl" py={widgetSectionPadding}>
      <Paper p={{ base: 24, md: 34 }} radius={40} withBorder style={getWidgetCardStyle(0, { emphasize: true })}>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
          <Stack gap={12}>
            {badge ? <Badge radius="xl" variant="light" style={widgetBadgeStyle}>{badge}</Badge> : null}
            {title ? <Title order={2} style={widgetTitleStyle}>{title}</Title> : null}
            {body ? <Text style={widgetSubtitleStyle}>{body}</Text> : null}

            {items.length ? (
              <Stack gap={8} mt={4}>
                {items.map((item, index) => (
                  <Group key={`${item.title || 'item'}-${index}`} align="flex-start" gap={10} wrap="nowrap">
                    <Box
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 999,
                        background: 'var(--brand-primary)',
                        color: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Text fw={600} style={{ color: 'var(--text-primary)' }}>{item.title || item.label}</Text>
                      {item.body ? <Text size="sm" style={{ ...widgetSubtitleStyle, maxWidth: 'unset' }}>{item.body}</Text> : null}
                    </Box>
                  </Group>
                ))}
              </Stack>
            ) : null}

            {(primaryHref || secondaryHref) ? (
              <Group mt={6}>
                {primaryHref ? (
                  <Button
                    component={Link}
                    href={primaryHref}
                    {...getCarComparisonLinkProps(primaryHref)}
                    styles={widgetPrimaryButtonStyles}
                  >
                    {primaryCtaLabel}
                  </Button>
                ) : null}
                {secondaryHref ? (
                  <Anchor
                    component={Link}
                    href={secondaryHref}
                    {...getCarComparisonLinkProps(secondaryHref)}
                    underline="never"
                    style={widgetLinkStyle}
                  >
                    {secondaryCtaLabel}
                  </Anchor>
                ) : null}
              </Group>
            ) : null}
          </Stack>

          {imageUrl ? (
            <MantineNextImage src={imageUrl} alt={imageAlt || title || 'Widget image'} radius={30} h={{ base: 260, md: 360 }} fit="cover" />
          ) : (
            <Paper radius={30} h={{ base: 260, md: 360 }} style={getWidgetCardStyle(2)} />
          )}
        </SimpleGrid>
      </Paper>
    </Container>
  );
}
